import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Check, ChevronLeft, ChevronRight, ClipboardCheck, FileSignature, Home, MapPin, Plus, RefreshCw, Save, Sprout, Trash2, UserRound, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import { httpServices } from "@/services/http/httpServices";
import type { GeographicUnit } from "@/types/domain";
import type { ConsentTemplateReference, FarmerGroupReference } from "@/services/contracts";
import { createOfflineDraft } from "@/offline/outbox";
import { executeOrQueue } from "@/offline/executeOrQueue";
import { useOffline } from "@/offline/OfflineProvider";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const stages = [
  { label: "Identity", icon: UserRound }, { label: "Contacts", icon: Home }, { label: "Location", icon: MapPin }, { label: "Household", icon: UsersRound }, { label: "Consent", icon: FileSignature }, { label: "Plots and crop establishment", icon: Sprout }, { label: "Review", icon: ClipboardCheck },
];
const fieldClass = "space-y-2";

type Sex = "" | "FEMALE" | "MALE" | "OTHER" | "NOT_STATED";
type ConsentState = "ACCEPTED" | "DECLINED" | "NOT_RECORDED";
type NextOfKinRelationship = "" | "SPOUSE" | "CHILD" | "PARENT" | "SIBLING" | "GRANDCHILD" | "OTHER";

type RegistrationPlotDraft = {
  id: string;
  name: string;
  area: string;
  ownershipType: string;
  currentLandUse: string;
  crop: string;
  variety: string;
  plantingDate: string;
  initialPopulation: string;
};

const newPlotDraft = (): RegistrationPlotDraft => ({
  id: crypto.randomUUID(),
  name: "",
  area: "",
  ownershipType: "",
  currentLandUse: "",
  crop: "",
  variety: "",
  plantingDate: "",
  initialPopulation: "",
});

export default function AddFarmerPage() {
  const offline = useOffline();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Stage 1: Identity
  const [name, setName] = useState({ first: "", last: "" });
  const [sex, setSex] = useState<Sex>("");
  const [birthYear, setBirthYear] = useState("");
  const [farmerGroupId, setFarmerGroupId] = useState("");
  const [farmerGroups, setFarmerGroups] = useState<FarmerGroupReference[]>([]);
  const [referencesLoading, setReferencesLoading] = useState(true);
  const [referencesError, setReferencesError] = useState("");
  const [nationalIdNumber, setNationalIdNumber] = useState("");
  const [nationalIdFront, setNationalIdFront] = useState<File | null>(null);
  const [nationalIdBack, setNationalIdBack] = useState<File | null>(null);
  const [farmerPhoto, setFarmerPhoto] = useState<File | null>(null);

  // Stage 2: Contacts
  const [primaryPhone, setPrimaryPhone] = useState("7");
  const [alternatePhone, setAlternatePhone] = useState("");
  const [email, setEmail] = useState("");

  // Stage 3: Location
  const [geographyLoading, setGeographyLoading] = useState(true);
  const [geographyError, setGeographyError] = useState("");
  const [districts, setDistricts] = useState<GeographicUnit[]>([]);
  const [counties, setCounties] = useState<GeographicUnit[]>([]);
  const [subcounties, setSubcounties] = useState<GeographicUnit[]>([]);
  const [parishes, setParishes] = useState<GeographicUnit[]>([]);
  const [localities, setLocalities] = useState<GeographicUnit[]>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<string>("");
  const [selectedCounty, setSelectedCounty] = useState<string>("");
  const [selectedSubcounty, setSelectedSubcounty] = useState<string>("");
  const [selectedParish, setSelectedParish] = useState<string>("");
  const [selectedLocality, setSelectedLocality] = useState<string>("");
  const [landmark, setLandmark] = useState("");

  // Stage 4: Household
  const [householdSize, setHouseholdSize] = useState("");
  const [dependantsInHousehold, setDependantsInHousehold] = useState("");
  const [dependantsElsewhere, setDependantsElsewhere] = useState("");
  const [dependantsUnder18, setDependantsUnder18] = useState("");
  const [householdNotes, setHouseholdNotes] = useState("");
  const [nextOfKinFirstName, setNextOfKinFirstName] = useState("");
  const [nextOfKinMiddleName, setNextOfKinMiddleName] = useState("");
  const [nextOfKinLastName, setNextOfKinLastName] = useState("");
  const [nextOfKinRelationship, setNextOfKinRelationship] = useState<NextOfKinRelationship>("");
  const [nextOfKinPhone, setNextOfKinPhone] = useState("");
  const [nextOfKinLivesInHousehold, setNextOfKinLivesInHousehold] = useState("YES");
  const [nextOfKinNotes, setNextOfKinNotes] = useState("");

  // Stage 5: Consent
  const [consentState, setConsentState] = useState<ConsentState>("NOT_RECORDED");
  const [consentTemplate, setConsentTemplate] = useState<ConsentTemplateReference | null>(null);
  const [signedAgreement, setSignedAgreement] = useState<File | null>(null);

  // Stage 6: Farm umbrella + any plots already known at registration.
  const [farmName, setFarmName] = useState("");
  const [plots, setPlots] = useState<RegistrationPlotDraft[]>(() => [newPlotDraft()]);

  const updatePlotDraft = (id: string, changes: Partial<RegistrationPlotDraft>) =>
    setPlots((current) => current.map((plot) => plot.id === id ? { ...plot, ...changes } : plot));
  const addPlot = () => setPlots((current) => [...current, newPlotDraft()]);
  const removePlot = (id: string) => setPlots((current) => current.length === 1 ? current : current.filter((plot) => plot.id !== id));

  useEffect(() => {
    let active = true;
    setReferencesLoading(true);
    setReferencesError("");
    Promise.allSettled([services.farmers.listGroups(), services.farmers.getActiveConsentTemplate()])
      .then(([groupsResult, consentResult]) => {
        if (!active) return;
        if (groupsResult.status === "fulfilled") {
          setFarmerGroups(groupsResult.value);
        } else {
          setReferencesError(groupsResult.reason instanceof Error ? groupsResult.reason.message : "Farmer groups could not be loaded.");
        }
        setConsentTemplate(consentResult.status === "fulfilled" ? consentResult.value : null);
      })
      .finally(() => { if (active) setReferencesLoading(false); });
    return () => { active = false; };
  }, []);

  // ── Geography: fetch districts ────────────────────────────────
  const loadDistricts = useCallback(() => {
    let active = true;
    setGeographyLoading(true);
    setGeographyError("");
    httpServices.geography.listUnits(undefined, "DISTRICT")
      .then((items) => { if (active) setDistricts(items); })
      .catch((reason: Error) => { if (active) setGeographyError(reason.message || "Official geography could not be loaded."); })
      .finally(() => { if (active) setGeographyLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => loadDistricts(), [loadDistricts]);

  // ── Geography: dependent cascade with race-condition guard ────
  useEffect(() => {
    if (!selectedDistrict) { setCounties([]); return; }
    let active = true;
    setCounties([]);
    setSelectedCounty("");
    setSubcounties([]);
    setSelectedSubcounty("");
    setParishes([]);
    setSelectedParish("");
    setLocalities([]);
    setSelectedLocality("");
    httpServices.geography.listUnits(selectedDistrict, "COUNTY")
      .then((items) => { if (active) setCounties(items); })
      .catch((reason: Error) => { if (active) setGeographyError(reason.message || "Counties could not be loaded."); });
    return () => { active = false; };
  }, [selectedDistrict]);

  useEffect(() => {
    if (!selectedCounty) { setSubcounties([]); return; }
    let active = true;
    setSubcounties([]);
    setSelectedSubcounty("");
    setParishes([]);
    setSelectedParish("");
    setLocalities([]);
    setSelectedLocality("");
    httpServices.geography.listUnits(selectedCounty)
      .then((items) => { if (active) setSubcounties(items); })
      .catch((reason: Error) => { if (active) setGeographyError(reason.message || "Subcounties could not be loaded."); });
    return () => { active = false; };
  }, [selectedCounty]);

  useEffect(() => {
    if (!selectedSubcounty) { setParishes([]); return; }
    let active = true;
    setParishes([]);
    setSelectedParish("");
    setLocalities([]);
    setSelectedLocality("");
    httpServices.geography.listUnits(selectedSubcounty)
      .then((items) => { if (active) setParishes(items); })
      .catch((reason: Error) => { if (active) setGeographyError(reason.message || "Parishes could not be loaded."); });
    return () => { active = false; };
  }, [selectedSubcounty]);

  useEffect(() => {
    if (!selectedParish) {
      setLocalities([]);
      setSelectedLocality("");
      return;
    }
    let active = true;
    setLocalities([]);
    setSelectedLocality("");
    httpServices.geography.listUnits(selectedParish)
      .then((items) => {
        if (!active) return;
        setLocalities(items.filter((item) => item.unitType === "VILLAGE" || item.unitType === "CELL"));
      })
      .catch((reason: Error) => {
        if (active) setGeographyError(reason.message || "Villages or cells could not be loaded.");
      });
    return () => { active = false; };
  }, [selectedParish]);

  // ── Geography labels ──────────────────────────────────────────
  const districtName = districts.find((d) => d.id === selectedDistrict)?.name || "";
  const countyName = counties.find((c) => c.id === selectedCounty)?.name || "";
  const subcountyName = subcounties.find((s) => s.id === selectedSubcounty)?.name || "";
  const parishName = parishes.find((p) => p.id === selectedParish)?.name || "";
  const localityName = localities.find((unit) => unit.id === selectedLocality)?.name || "";
  const locationLabel = [districtName, countyName, subcountyName, parishName, localityName].filter(Boolean).join(" / ");
  const selectedGroup = farmerGroups.find((group) => group.id === farmerGroupId);
  const currentYear = new Date().getFullYear();
  const parsedBirthYear = birthYear.trim() ? Number(birthYear) : undefined;
  const birthYearValid = parsedBirthYear === undefined || (Number.isInteger(parsedBirthYear) && parsedBirthYear >= 1900 && parsedBirthYear <= currentYear);
  const parsedHouseholdSize = householdSize.trim() ? Number(householdSize) : undefined;
  const parsedDependantsInHousehold = dependantsInHousehold.trim() ? Number(dependantsInHousehold) : undefined;
  const parsedDependantsElsewhere = dependantsElsewhere.trim() ? Number(dependantsElsewhere) : undefined;
  const parsedDependantsUnder18 = dependantsUnder18.trim() ? Number(dependantsUnder18) : undefined;
  const parsedDependants =
    parsedDependantsInHousehold !== undefined && parsedDependantsElsewhere !== undefined
      ? parsedDependantsInHousehold + parsedDependantsElsewhere
      : undefined;
  const householdValid =
    [parsedHouseholdSize, parsedDependantsInHousehold, parsedDependantsElsewhere, parsedDependantsUnder18]
      .every((value) => value === undefined || (Number.isInteger(value) && value >= 0)) &&
    !(parsedHouseholdSize !== undefined && parsedDependantsInHousehold !== undefined && parsedDependantsInHousehold > parsedHouseholdSize) &&
    !(parsedDependants !== undefined && parsedDependantsUnder18 !== undefined && parsedDependantsUnder18 > parsedDependants);

  const primaryPhoneDigits = primaryPhone.replace(/\D/g, "");
  const primaryPhoneValid = /^7\d{8}$/.test(primaryPhoneDigits);
  const normalizedPrimaryPhone = primaryPhoneValid ? `+256${primaryPhoneDigits}` : "";
  const phoneValid = (value: string) => !value.trim() || (value.trim().length >= 5 && value.trim().length <= 40);
  const contactValid =
    primaryPhoneValid &&
    phoneValid(alternatePhone) &&
    (!email.trim() || (email.trim().length <= 320 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())));
  const hasContact = true;

  const ninNormalized = nationalIdNumber.toUpperCase().replace(/\s+/g, "");
  const ninValid = !ninNormalized || (/^[A-Z0-9]{6,32}$/.test(ninNormalized));
  const imageTypes = ["image/jpeg", "image/png", "image/webp"];
  const agreementTypes = ["application/pdf", ...imageTypes];
  const evidenceFileValid = (file: File | null, allowed: string[], maxBytes: number) => !file || (allowed.includes(file.type) && file.size > 0 && file.size <= maxBytes);
  const identityEvidenceValid =
    evidenceFileValid(nationalIdFront, imageTypes, 10_485_760) &&
    evidenceFileValid(nationalIdBack, imageTypes, 10_485_760) &&
    evidenceFileValid(farmerPhoto, imageTypes, 5_242_880);
  const nextOfKinValid =
    !!nextOfKinFirstName.trim() &&
    !!nextOfKinLastName.trim() &&
    !!nextOfKinRelationship &&
    nextOfKinPhone.trim().length >= 5 &&
    nextOfKinPhone.trim().length <= 40;
  const signedAgreementValid = evidenceFileValid(signedAgreement, agreementTypes, 10_485_760);
  const hasSensitiveRegistrationData = true;

  const meaningfulPlots = plots.filter((plot) =>
    !!(plot.name.trim() || plot.area.trim() || plot.ownershipType || plot.currentLandUse.trim() ||
       plot.crop.trim() || plot.variety.trim() || plot.plantingDate || plot.initialPopulation.trim())
  );
  const plotDraftValid = (plot: RegistrationPlotDraft) => {
    const area = plot.area.trim() ? Number(plot.area) : undefined;
    const population = plot.initialPopulation.trim() ? Number(plot.initialPopulation) : undefined;
    const cropDetailEntered = !!(plot.variety.trim() || plot.plantingDate || plot.initialPopulation.trim());
    return (
      (area === undefined || (Number.isFinite(area) && area > 0)) &&
      (population === undefined || (Number.isInteger(population) && population >= 0)) &&
      (!cropDetailEntered || !!plot.crop.trim())
    );
  };
  const landSetupValid = plots.every(plotDraftValid);
  const derivedFarmAcreage = meaningfulPlots.reduce((sum, plot) => {
    const area = Number(plot.area);
    return sum + (Number.isFinite(area) && area > 0 ? area : 0);
  }, 0);
  const consentStageValid = consentState !== "DECLINED" && signedAgreementValid;

  // ── Stage validation ───────────────────────────────────────────
  const stageValid: boolean[] = [
    // Identity
    name.first.trim().length > 0 && name.last.trim().length > 0 && !!sex && birthYearValid && ninValid && identityEvidenceValid,
    // Contacts — primary Ugandan mobile number is required
    contactValid && !(offline.simulatedOffline || !offline.online),
    // Location
    !!selectedLocality,
    // Household + required primary next of kin
    householdValid && nextOfKinValid,
    // Consent/agreement may remain pending while the farmer stays in DRAFT.
    consentStageValid,
    // Farm name plus any plot details already known; detailed mapping comes later in Farms.
    landSetupValid,
    // Review
    name.first.trim().length > 0 && name.last.trim().length > 0 && !!sex && birthYearValid && ninValid && identityEvidenceValid && contactValid && householdValid && nextOfKinValid && !!selectedLocality && consentStageValid && landSetupValid,
  ];

  const next = () => {
    if (!stageValid[step]) {
      toast("Complete required fields before continuing", { description: getStageValidationHint(step) });
      return;
    }
    setStep((value) => Math.min(value + 1, stages.length - 1));
  };
  const previous = () => setStep((value) => Math.max(value - 1, 0));

  function getStageValidationHint(stage: number): string {
    switch (stage) {
      case 0: return "First name, last name and gender are required. If a NIN or identity image is supplied, it must use a valid format and file size.";
      case 1: return offline.simulatedOffline || !offline.online ? "Reconnect before registering: the mandatory primary phone is encrypted and is never stored in an offline draft." : "Enter a valid Ugandan mobile number: +256 followed by 7 and eight more digits.";
      case 2: return "Select district, county, subcounty/town council, parish/ward and village/cell.";
      case 3: return "Household counts must be non-negative. Dependants living here plus dependants living elsewhere form the total; the primary next of kin also requires name, relationship and phone.";
      case 4: return consentTemplate ? "Do not select Declined. The farmer may remain in Draft with an agreement still pending; any selected file must be a PDF/JPEG/PNG/WebP not exceeding 10 MB." : "No approved agreement template is loaded yet. Registration can continue in Draft and the agreement will remain outstanding.";
      case 5: return "Farm name is optional. For any plot details entered, acreage must be greater than zero and planting details require a crop.";
      default: return "Review the required fields and try again.";
    }
  }

  // ── Submit ─────────────────────────────────────────────────────
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.first.trim() || !name.last.trim()) { setStep(0); toast("Complete required identity fields", { description: "First and last name are required." }); return; }
    if (!sex || !birthYearValid || !ninValid || !identityEvidenceValid) { setStep(0); toast("Complete the identity details", { description: "Check gender, birth year, NIN and selected identity/photo files." }); return; }
    if (!contactValid) { setStep(1); toast("Correct the contact details", { description: "A valid Ugandan primary mobile number (+2567XXXXXXXX) is required; check any email entered as well." }); return; }
    if (offline.simulatedOffline || !offline.online) { setStep(1); toast("Reconnect before saving contact details", { description: "The mandatory phone and any email are encrypted and never written to the unencrypted offline queue." }); return; }
    if (!selectedLocality) { setStep(2); toast("Complete the official location", { description: "Select a district, county, subcounty or urban unit, parish or ward, and village or cell." }); return; }
    if (!householdValid || !nextOfKinValid) { setStep(3); toast("Complete the household and next of kin", { description: "A primary next of kin requires name, relationship and phone." }); return; }
    if (!consentStageValid) { setStep(4); toast("Check the agreement step", { description: getStageValidationHint(4) }); return; }
    if (!landSetupValid) { setStep(5); toast("Check the optional farm and plot details", { description: getStageValidationHint(5) }); return; }
    if (hasSensitiveRegistrationData && (offline.simulatedOffline || !offline.online)) { toast("Reconnect before completing registration", { description: "NIN, next-of-kin contact and evidence files are never placed in the offline queue." }); return; }

    const entityId = crypto.randomUUID();
    const capturedAt = new Date().toISOString();
    const geographicUnitLabel = locationLabel;
    const contact = {
      primaryPhone: normalizedPrimaryPhone,
      alternativePhone: alternatePhone.trim() || undefined,
      email: email.trim() || undefined,
    };
    const hasHousehold =
      parsedHouseholdSize !== undefined || parsedDependantsInHousehold !== undefined ||
      parsedDependantsElsewhere !== undefined || parsedDependantsUnder18 !== undefined ||
      !!householdNotes.trim();
    const household = hasHousehold ? {
      declaredHouseholdSize: parsedHouseholdSize,
      declaredDependants: parsedDependants,
      dependantsInHousehold: parsedDependantsInHousehold,
      dependantsElsewhere: parsedDependantsElsewhere,
      dependantsUnder18: parsedDependantsUnder18,
      notes: householdNotes.trim() || undefined,
    } : undefined;
    const farmId = crypto.randomUUID();
    const initialFarm = {
      clientGeneratedId: farmId,
      farmName: farmName.trim() || `${name.first.trim()} ${name.last.trim()} Farm`,
      geographicUnitId: selectedLocality,
    };
    const nextOfKin = { firstName: nextOfKinFirstName.trim(), middleName: nextOfKinMiddleName.trim() || undefined, lastName: nextOfKinLastName.trim(), relationshipType: nextOfKinRelationship as Exclude<NextOfKinRelationship, "">, phone: nextOfKinPhone.trim(), livesInHousehold: nextOfKinLivesInHousehold === "YES", notes: nextOfKinNotes.trim() || undefined };
    const agreementAccepted = !!consentTemplate && consentState === "ACCEPTED";
    const domainInput = {
      firstName: name.first.trim(),
      lastName: name.last.trim(),
      sex: sex as Exclude<Sex, "">,
      nationality: "UGANDA",
      geographicUnitId: selectedLocality,
      geographicUnitLabel,
      consentObtained: agreementAccepted,
      consentTemplateId: agreementAccepted ? consentTemplate?.id : undefined,
      farmerGroupId: farmerGroupId || undefined,
      farmerGroupName: selectedGroup?.groupName,
      birthYearEstimated: parsedBirthYear,
      nationalIdNumber: ninNormalized || undefined,
      contact,
      household,
      nextOfKin,
      initialFarm,
    };
    const apiPayload = {
      client_generated_id: entityId,
      first_name: domainInput.firstName,
      last_name: domainInput.lastName,
      sex: domainInput.sex,
      nationality: domainInput.nationality,
      geographic_unit_id: domainInput.geographicUnitId,
      geographic_unit_label: domainInput.geographicUnitLabel,
      consent_obtained: domainInput.consentObtained,
      consent_template_id: domainInput.consentTemplateId,
      farmer_group_id: domainInput.farmerGroupId,
      farmer_group_name: domainInput.farmerGroupName,
      birth_year_estimated: domainInput.birthYearEstimated,
      national_id_number: domainInput.nationalIdNumber,
      contact: { primary_phone: contact.primaryPhone, alternative_phone: contact.alternativePhone, email: contact.email },
      household: household ? {
        declared_household_size: household.declaredHouseholdSize,
        declared_dependants: household.declaredDependants,
        dependants_in_household: household.dependantsInHousehold,
        dependants_elsewhere: household.dependantsElsewhere,
        dependants_under_18: household.dependantsUnder18,
        notes: household.notes,
      } : undefined,
      next_of_kin: { first_name: nextOfKin.firstName, middle_name: nextOfKin.middleName, last_name: nextOfKin.lastName, relationship_type: nextOfKin.relationshipType, phone: nextOfKin.phone, lives_in_household: nextOfKin.livesInHousehold, notes: nextOfKin.notes },
      initial_farm: { client_generated_id: initialFarm.clientGeneratedId, farm_name: initialFarm.farmName, geographic_unit_id: initialFarm.geographicUnitId },
      actual_activity_datetime: capturedAt,
      local_created_at: capturedAt,
      created_offline: false,
    };
    try {
      const result = await executeOrQueue({ offline: offline.simulatedOffline || !offline.online, request: { entityType: "FARMER", entityId, method: "POST", path: "/farmers", payload: apiPayload }, execute: () => services.farmers.create(domainInput, entityId) });
      if (result.mode === "SYNCED") {
        const evidence: Array<["national-id-front" | "national-id-back" | "farmer-photo" | "signed-agreement", File]> = [];
        if (nationalIdFront) evidence.push(["national-id-front", nationalIdFront]);
        if (nationalIdBack) evidence.push(["national-id-back", nationalIdBack]);
        if (farmerPhoto) evidence.push(["farmer-photo", farmerPhoto]);
        if (signedAgreement && agreementAccepted) evidence.push(["signed-agreement", signedAgreement]);
        const failed: string[] = [];
        for (const [type, file] of evidence) {
          try { await services.farmers.uploadEvidence(result.value.id, type, file); }
          catch { failed.push(type); }
        }

        const plotWarnings: string[] = [];
        for (let index = 0; index < meaningfulPlots.length; index += 1) {
          const plot = meaningfulPlots[index];
          try {
            const parsedArea = plot.area.trim() ? Number(plot.area) : undefined;
            const parsedPopulation = plot.initialPopulation.trim() ? Number(plot.initialPopulation) : undefined;
            const createdPlot = await services.farms.createPlot({
              farmId,
              plotName: plot.name.trim() || `Plot ${index + 1}`,
              reportedAreaAcres: parsedArea,
              ownershipType: plot.ownershipType || undefined,
              currentLandUse: plot.currentLandUse.trim() || plot.crop.trim() || undefined,
            });
            if (plot.crop.trim()) {
              await services.farms.createCropEstablishment(createdPlot.id, {
                crop: plot.crop.trim(),
                variety: plot.variety.trim() || undefined,
                plantingDate: plot.plantingDate || undefined,
                initialPlantPopulation: parsedPopulation,
                productionStage: "NEWLY_ESTABLISHED",
              });
            }
          } catch (reason) {
            plotWarnings.push(`Plot ${index + 1}: ${reason instanceof Error ? reason.message : "setup needs retry"}`);
          }
        }
        const landSetupWarning = plotWarnings.join("; ");

        const hasWarning = failed.length > 0 || !!landSetupWarning;
        toast(hasWarning ? "Farmer saved; optional follow-up needed" : "Farmer registered", {
          description: failed.length
            ? `Farmer saved. Evidence uploads needing retry: ${failed.join(", ")}.${landSetupWarning ? ` Plot setup: ${landSetupWarning}` : ""}`
            : landSetupWarning
              ? `Farmer saved. Optional plot setup can be completed from the registration workspace: ${landSetupWarning}`
              : agreementAccepted ? "Farmer, farm and registration plot details were saved. Detailed mapping can continue from Farms." : "Farmer, farm and registration plot details were saved in Draft. The general farmer agreement remains outstanding until the approved template is available and signed.",
        });
        navigate(`/farmers/${result.value.id}`);
      } else {
        toast("Farmer registration queued", { description: "The durable draft will synchronize after reconnect." });
        navigate("/farmers");
      }
    } catch (reason) {
      toast("Farmer registration not saved", { description: reason instanceof Error ? reason.message : "Review the required fields and try again." });
    }
  };

  const saveDraft = async () => {
    await createOfflineDraft("FARMER", {
      name, sex, birthYear, farmerGroupId,
      selectedDistrict, selectedCounty, selectedSubcounty, selectedParish, selectedLocality, landmark,
      householdSize, dependantsInHousehold, dependantsElsewhere, dependantsUnder18, householdNotes,
      consentState, consentTemplateId: consentTemplate?.id,
      farmName, plots,
      step,
    });
    toast("Farmer draft saved", { description: hasContact || hasSensitiveRegistrationData ? "Saved without phone, NIN, next-of-kin details or evidence files; sensitive values are never placed in the offline draft." : "Stored locally on this device in IndexedDB." });
  };

  return (
    <div className="space-y-6" data-testid="add-farmer-page">
      <PageHeader eyebrow="Farmer registration" title="Add Farmer" description="A seven-stage workflow aligned to the approved farmer, consent, household, location and farm registry structure." backTo="/farmers" />
      <div className="rounded-xl border border-accent/25 bg-accent/10 px-4 py-3 text-sm text-accent-foreground" data-testid="add-farmer-registry-alert"><strong>Connected registry:</strong> submitting this form saves the supported farmer fields to the active ARISE environment.</div>

      <div className="grid gap-6 xl:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="hidden xl:block" data-testid="add-farmer-stage-tabs">
          <Card className="sticky top-24 border-border/70 shadow-soft"><CardContent className="p-3">{stages.map(({ label, icon: Icon }, index) => <button key={label} type="button" onClick={() => setStep(index)} className={`flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm transition-[background-color,color] ${step === index ? "bg-primary text-primary-foreground" : index < step ? "text-primary hover:bg-secondary" : "text-muted-foreground hover:bg-secondary"}`} data-testid={`registration-stage-${index + 1}`}><span className={`flex size-7 items-center justify-center rounded-full border ${step === index ? "border-white/30" : index < step ? "border-primary/30 bg-primary/10" : "border-border"}`}>{index < step ? <Check className="size-3.5" /> : <Icon className="size-3.5" />}</span><span>{index + 1}. {label}</span></button>)}</CardContent></Card>
        </aside>

        <form onSubmit={submit} className="space-y-5" data-testid="add-farmer-form">
          {referencesError && <div className="rounded-lg border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive" data-testid="registration-reference-error">Registration references are unavailable: {referencesError}. Farmer groups and consent cannot be guessed; retry after the API is restored.</div>}
          <div className="xl:hidden" data-testid="registration-mobile-progress"><div className="mb-2 flex items-center justify-between text-xs"><span className="font-semibold">Step {step + 1} of 7 · {stages[step].label}</span><span className="text-muted-foreground">{Math.round(((step + 1) / 7) * 100)}%</span></div><Progress value={((step + 1) / 7) * 100} /></div>

          <section className="form-section min-h-[420px]" data-testid={`registration-stage-content-${step + 1}`}>
            <div className="mb-6 flex items-start justify-between gap-4"><div><Badge variant="secondary">Stage {step + 1}</Badge><h2 className="mt-3 text-xl font-semibold">{stages[step].label}</h2><p className="mt-1 text-sm text-muted-foreground">{["Capture the farmer's operational identity.", "Add secure contact channels.", "Select the official administrative hierarchy.", "Record household composition without sensitive values.", "Confirm the approved consent template.", "Add optional Plot and crop-establishment details to the farmer's Farm.", "Review this registration before saving."][step]}</p></div></div>

            {/* Stage 1: Identity */}
            {step === 0 && <div className="grid gap-5 md:grid-cols-2">
              <div className={fieldClass}>
                <Label htmlFor="firstName">First name <span className="text-destructive">*</span></Label>
                <Input id="firstName" value={name.first} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName({ ...name, first: e.target.value })} placeholder="Enter first name" data-testid="farmer-first-name-input" />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="lastName">Last name <span className="text-destructive">*</span></Label>
                <Input id="lastName" value={name.last} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName({ ...name, last: e.target.value })} placeholder="Enter last name" data-testid="farmer-last-name-input" />
              </div>
              <div className={fieldClass}>
                <Label>Gender <span className="text-destructive">*</span></Label>
                <Select value={sex} onValueChange={(value: string) => setSex(value as Sex)}>
                  <SelectTrigger data-testid="farmer-gender-select"><SelectValue placeholder="Select gender" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FEMALE">Female</SelectItem>
                    <SelectItem value="MALE">Male</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                    <SelectItem value="NOT_STATED">Prefer not to state</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className={fieldClass}>
                <Label htmlFor="birthYear">Year of birth</Label>
                <Input id="birthYear" type="number" min="1900" max={currentYear} value={birthYear} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBirthYear(e.target.value)} placeholder="Optional, e.g. 1984" data-testid="farmer-birth-year-input" />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="nationalIdNumber">National ID / NIN</Label>
                <Input id="nationalIdNumber" value={nationalIdNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNationalIdNumber(e.target.value.toUpperCase())} placeholder="Optional — enter NIN when available" data-testid="farmer-nin-input" />
                <p className="text-xs text-muted-foreground">Encrypted before storage. Only a masked suffix should be shown during normal use.</p>
              </div>
              <div className={fieldClass}>
                <Label htmlFor="farmerPhoto">Farmer photograph</Label>
                <Input id="farmerPhoto" type="file" accept="image/jpeg,image/png,image/webp" capture="user" onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFarmerPhoto(e.target.files?.[0] || null)} data-testid="farmer-photo-input" />
                <p className="text-xs text-muted-foreground">{farmerPhoto ? farmerPhoto.name : "Take or upload a recent registration photograph (max 5 MB)."}</p>
              </div>
              <div className={fieldClass}>
                <Label htmlFor="nationalIdFront">National ID — front</Label>
                <Input id="nationalIdFront" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNationalIdFront(e.target.files?.[0] || null)} data-testid="farmer-id-front-input" />
                <p className="text-xs text-muted-foreground">{nationalIdFront ? nationalIdFront.name : "Optional clear image, max 10 MB."}</p>
              </div>
              <div className={fieldClass}>
                <Label htmlFor="nationalIdBack">National ID — back</Label>
                <Input id="nationalIdBack" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNationalIdBack(e.target.files?.[0] || null)} data-testid="farmer-id-back-input" />
                <p className="text-xs text-muted-foreground">{nationalIdBack ? nationalIdBack.name : "Optional clear image, max 10 MB."}</p>
              </div>
              <div className={`${fieldClass} md:col-span-2`}>
                <Label>Farmer group</Label>
                <Select value={farmerGroupId} onValueChange={setFarmerGroupId} disabled={referencesLoading || !!referencesError || farmerGroups.length === 0}>
                  <SelectTrigger data-testid="farmer-group-input"><SelectValue placeholder={farmerGroups.length ? "Select an existing group" : "No active farmer groups available"} /></SelectTrigger>
                  <SelectContent>{farmerGroups.map((group) => <SelectItem key={group.id} value={group.id}>{group.groupName} · {group.groupCode}</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">Optional. Only active groups in the current ARISE project are available.</p>
                  <Button type="button" variant="link" size="sm" asChild><Link to="/farmer-groups">Manage groups</Link></Button>
                </div>
              </div>
            </div>}

            {/* Stage 2: Contacts */}
            {step === 1 && <div className="grid gap-5 md:grid-cols-2">
              <div className={fieldClass}>
                <Label htmlFor="primaryPhone">Primary phone <span className="text-destructive">*</span></Label>
                <div className="flex">
                  <span className="inline-flex items-center rounded-l-md border border-r-0 bg-muted px-3 text-sm font-medium">+256</span>
                  <Input
                    id="primaryPhone"
                    type="tel"
                    inputMode="numeric"
                    maxLength={9}
                    value={primaryPhone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let digits = e.target.value.replace(/\D/g, "");
                      if (digits.startsWith("256")) digits = digits.slice(3);
                      if (digits.startsWith("0")) digits = digits.slice(1);
                      setPrimaryPhone(digits.slice(0, 9));
                    }}
                    placeholder="7XXXXXXXX"
                    className="rounded-l-none"
                    aria-invalid={!primaryPhoneValid}
                    data-testid="farmer-phone-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Required Uganda mobile format: +256 7XX XXX XXX.</p>
              </div>
              <div className={fieldClass}>
                <Label htmlFor="alternatePhone">Alternate phone</Label>
                <Input id="alternatePhone" type="tel" value={alternatePhone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAlternatePhone(e.target.value)} placeholder="Optional" data-testid="farmer-alt-phone-input" />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="email">Email address</Label>
                <Input id="email" type="email" value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} placeholder="Optional" data-testid="farmer-email-input" />
              </div>
              <p className="md:col-span-2 rounded-lg border border-border bg-muted/45 p-3 text-xs leading-5 text-muted-foreground" data-testid="contact-privacy-note">Phone and email values are encrypted by the backend and stored only in the restricted private contact table. For safety, contact details cannot be queued or saved in an offline draft.</p>
            </div>}

            {/* Stage 3: Location */}
            {step === 2 && <div className="space-y-4">
              {geographyLoading && <p className="rounded-lg border border-border bg-muted/45 p-3 text-sm text-muted-foreground" data-testid="geography-loading">Loading official UBOS geography…</p>}
              {geographyError && <div className="rounded-lg border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive" data-testid="geography-error">
                <div className="flex items-center justify-between gap-3">
                  <span>Official geography is unavailable: {geographyError}</span>
                  <Button type="button" variant="outline" size="sm" onClick={() => loadDistricts()} data-testid="geography-retry-button"><RefreshCw className="mr-2 size-3.5" />Retry</Button>
                </div>
              </div>}
              {!geographyLoading && !geographyError && districts.length === 0 && <p className="rounded-lg border border-border bg-muted/45 p-3 text-sm text-muted-foreground" data-testid="geography-empty">No geography records are available for your access scope.</p>}
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                <div className={fieldClass}>
                  <Label>District <span className="text-destructive">*</span></Label>
                  <Select value={selectedDistrict} onValueChange={setSelectedDistrict} disabled={geographyLoading || !!geographyError}>
                    <SelectTrigger data-testid="add-farmer-district-input"><SelectValue placeholder="Select district" /></SelectTrigger>
                    <SelectContent>{districts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className={fieldClass}>
                  <Label>County <span className="text-destructive">*</span></Label>
                  <Select value={selectedCounty} onValueChange={setSelectedCounty} disabled={!selectedDistrict}>
                    <SelectTrigger data-testid="add-farmer-county-input"><SelectValue placeholder={selectedDistrict ? "Select county" : "Select district first"} /></SelectTrigger>
                    <SelectContent>{counties.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className={fieldClass}>
                  <Label>Subcounty / Town Council <span className="text-destructive">*</span></Label>
                  <Select value={selectedSubcounty} onValueChange={setSelectedSubcounty} disabled={!selectedCounty}>
                    <SelectTrigger data-testid="add-farmer-subcounty-input"><SelectValue placeholder={selectedCounty ? "Select unit" : "Select county first"} /></SelectTrigger>
                    <SelectContent>{subcounties.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className={fieldClass}>
                  <Label>Parish / Ward <span className="text-destructive">*</span></Label>
                  <Select value={selectedParish} onValueChange={setSelectedParish} disabled={!selectedSubcounty}>
                    <SelectTrigger data-testid="add-farmer-parish-input"><SelectValue placeholder={selectedSubcounty ? "Select parish" : "Select subcounty first"} /></SelectTrigger>
                    <SelectContent>{parishes.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className={fieldClass}>
                  <Label>Village / Cell <span className="text-destructive">*</span></Label>
                  <Select value={selectedLocality} onValueChange={setSelectedLocality} disabled={!selectedParish}>
                    <SelectTrigger data-testid="add-farmer-village-input"><SelectValue placeholder={selectedParish ? (localities.length ? "Select village or cell" : "No villages or cells available") : "Select parish or ward first"} /></SelectTrigger>
                    <SelectContent>{localities.map((unit) => <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {selectedParish && localities.length === 0 && <p className="text-xs text-muted-foreground">No official village/cell records are available under this parish or ward yet.</p>}
                </div>
                <div className={`${fieldClass} md:col-span-2 xl:col-span-3`}>
                  <Label htmlFor="landmark">Nearest landmark</Label>
                  <Input id="landmark" value={landmark} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLandmark(e.target.value)} placeholder="Optional field navigation note" data-testid="location-landmark-input" />
                </div>
              </div>
            </div>}

            {/* Stage 4: Household */}
            {step === 3 && <div className="grid gap-5 md:grid-cols-2">
              <div className={fieldClass}>
                <Label htmlFor="householdSize">Household size</Label>
                <Input id="householdSize" type="number" min="0" value={householdSize} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHouseholdSize(e.target.value)} placeholder="People who live in this household" data-testid="household-size-input" />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="totalDependants">Total dependants</Label>
                <Input id="totalDependants" type="number" value={parsedDependants ?? ""} readOnly placeholder="Calculated from the two fields below" data-testid="household-total-dependants-input" />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="dependantsInHousehold">Dependants living in this household</Label>
                <Input id="dependantsInHousehold" type="number" min="0" value={dependantsInHousehold} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDependantsInHousehold(e.target.value)} placeholder="Optional" data-testid="household-dependants-home-input" />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="dependantsElsewhere">Dependants living elsewhere</Label>
                <Input id="dependantsElsewhere" type="number" min="0" value={dependantsElsewhere} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDependantsElsewhere(e.target.value)} placeholder="Optional" data-testid="household-dependants-elsewhere-input" />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="dependantsUnder18">Dependants under 18</Label>
                <Input id="dependantsUnder18" type="number" min="0" value={dependantsUnder18} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDependantsUnder18(e.target.value)} placeholder="Optional" data-testid="household-dependants-under18-input" />
                <p className="text-xs text-muted-foreground">This is an age subset of total dependants, not an additional count.</p>
              </div>
              <div className="md:col-span-2 mt-2 rounded-lg border border-border bg-muted/20 p-4">
                <p className="font-semibold">Primary next of kin / responsible person <span className="text-destructive">*</span></p>
                <p className="mt-1 text-xs text-muted-foreground">The person ACZON should contact when the registered farmer is unavailable.</p>
              </div>
              <div className={fieldClass}>
                <Label htmlFor="nokFirstName">First name <span className="text-destructive">*</span></Label>
                <Input id="nokFirstName" value={nextOfKinFirstName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNextOfKinFirstName(e.target.value)} data-testid="nok-first-name-input" />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="nokMiddleName">Middle name</Label>
                <Input id="nokMiddleName" value={nextOfKinMiddleName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNextOfKinMiddleName(e.target.value)} data-testid="nok-middle-name-input" />
              </div>
              <div className={fieldClass}>
                <Label htmlFor="nokLastName">Last name <span className="text-destructive">*</span></Label>
                <Input id="nokLastName" value={nextOfKinLastName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNextOfKinLastName(e.target.value)} data-testid="nok-last-name-input" />
              </div>
              <div className={fieldClass}>
                <Label>Relationship <span className="text-destructive">*</span></Label>
                <Select value={nextOfKinRelationship} onValueChange={(value: string) => setNextOfKinRelationship(value as NextOfKinRelationship)}>
                  <SelectTrigger data-testid="nok-relationship-select"><SelectValue placeholder="Select relationship" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SPOUSE">Spouse</SelectItem>
                    <SelectItem value="CHILD">Child</SelectItem>
                    <SelectItem value="PARENT">Parent</SelectItem>
                    <SelectItem value="SIBLING">Sibling</SelectItem>
                    <SelectItem value="GRANDCHILD">Grandchild</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className={fieldClass}>
                <Label htmlFor="nokPhone">Phone <span className="text-destructive">*</span></Label>
                <Input id="nokPhone" type="tel" value={nextOfKinPhone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNextOfKinPhone(e.target.value)} placeholder="+256…" data-testid="nok-phone-input" />
                <p className="text-xs text-muted-foreground">Encrypted in the restricted household contact table.</p>
              </div>
              <div className={fieldClass}>
                <Label>Lives in the farmer's household?</Label>
                <Select value={nextOfKinLivesInHousehold} onValueChange={setNextOfKinLivesInHousehold}>
                  <SelectTrigger data-testid="nok-household-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="YES">Yes</SelectItem><SelectItem value="NO">No</SelectItem></SelectContent>
                </Select>
              </div>
              <div className={`${fieldClass} md:col-span-2`}>
                <Label htmlFor="nokNotes">Next-of-kin notes</Label>
                <Textarea id="nokNotes" value={nextOfKinNotes} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNextOfKinNotes(e.target.value)} placeholder="Optional contact or responsibility note" data-testid="nok-notes-input" />
              </div>
              <div className={`${fieldClass} md:col-span-2`}>
                <Label htmlFor="householdNotes">Household notes</Label>
                <Textarea id="householdNotes" value={householdNotes} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setHouseholdNotes(e.target.value)} placeholder="Optional non-sensitive household note" data-testid="household-notes-input" />
              </div>
              <p className="md:col-span-2 rounded-lg border border-border bg-muted/45 p-3 text-xs leading-5 text-muted-foreground" data-testid="household-persistence-note">Household size records people living in this home. Total dependants is calculated from dependants living here plus dependants living elsewhere; under-18 is an age subset.</p>
            </div>}

            {/* Stage 5: Farmer agreement */}
            {step === 4 && <div className="space-y-5">
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-5" data-testid="consent-template-card">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold">Farmer agreement</p>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">{consentTemplate ? `${consentTemplate.consentCode} · Version ${consentTemplate.version}` : referencesLoading ? "Checking approved agreement…" : "Updated agreement template still pending"}</p>
                  </div>
                  <Badge className={consentTemplate ? "bg-primary text-primary-foreground" : ""} variant={consentTemplate ? "default" : "secondary"}>{consentTemplate ? "Approved template" : "Setup pending"}</Badge>
                </div>
                <p className="mt-4 text-sm leading-6 text-muted-foreground">
                  {consentTemplate?.exactText || "The updated ACZON farmer agreement has not yet been loaded. You may register this farmer in Draft now; the agreement will remain an outstanding requirement and must be completed before approval."}
                </p>
                {consentTemplate && <p className="mt-3 text-xs text-muted-foreground">Effective from {consentTemplate.effectiveFrom} · Content hash {consentTemplate.contentSha256.slice(0, 12)}…</p>}
              </div>

              {consentTemplate ? <>
                <div className={fieldClass}>
                  <Label>Agreement outcome</Label>
                  <Select value={consentState} onValueChange={(value: string) => setConsentState(value as ConsentState)}>
                    <SelectTrigger data-testid="consent-outcome-select"><SelectValue placeholder="Select outcome" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACCEPTED">Accepted / signed</SelectItem>
                      <SelectItem value="NOT_RECORDED">Not yet completed</SelectItem>
                      <SelectItem value="DECLINED">Declined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className={fieldClass}>
                  <Label htmlFor="signedAgreement">Signed farmer agreement</Label>
                  <Input id="signedAgreement" type="file" accept="application/pdf,image/jpeg,image/png,image/webp" capture="environment" disabled={consentState !== "ACCEPTED"} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignedAgreement(e.target.files?.[0] || null)} data-testid="signed-agreement-input" />
                  <p className="text-xs text-muted-foreground">{signedAgreement ? signedAgreement.name : consentState === "ACCEPTED" ? "Upload the signed agreement as PDF or clear photo (max 10 MB), or add it later before approval." : "Select Accepted / signed to attach the signed agreement."}</p>
                </div>
              </> : (
                <div className="rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm">
                  <p className="font-semibold">Agreement requirement will stay pending</p>
                  <p className="mt-1 text-muted-foreground">The farmer and Farm can still be created. Once the approved agreement is loaded, this Farmer Registry record will show that the agreement is outstanding until it is signed and uploaded.</p>
                </div>
              )}
              {consentState === "DECLINED" && <p className="rounded-lg border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive" data-testid="consent-declined-warning">A declined agreement cannot be used to enrol the farmer. Change the outcome to Not yet completed if the agreement is simply pending.</p>}
            </div>}

            {/* Stage 6: Farm and registration plots */}
            {step === 5 && <div className="space-y-5">
              <div className="rounded-xl border bg-muted/20 p-5">
                <div className="grid gap-5 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <p className="font-semibold">Farm</p>
                    <p className="mt-1 text-sm text-muted-foreground">Every registered farmer receives one Farm record. Farm acreage is calculated automatically from its active Plots; ownership is recorded on each Plot.</p>
                  </div>
                  <div className={fieldClass}>
                    <Label htmlFor="farmName">Farm name</Label>
                    <Input id="farmName" value={farmName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFarmName(e.target.value)} placeholder={`${name.first || "Farmer"} ${name.last || ""} Farm`} data-testid="farm-name-input" />
                  </div>
                  <div className={fieldClass}>
                    <Label>Total Farm acreage</Label>
                    <Input value={derivedFarmAcreage > 0 ? derivedFarmAcreage.toFixed(2) : ""} readOnly placeholder="Calculated from Plot acreages" data-testid="farm-derived-area" />
                    <p className="text-xs text-muted-foreground">Not entered separately.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">Plots & crop establishment</p>
                  <p className="mt-1 text-sm text-muted-foreground">Capture whatever is already known. Detailed GPS/boundary mapping and later assessments continue from the Farms module.</p>
                </div>
                <Button type="button" variant="outline" onClick={addPlot}><Plus className="mr-2 size-4" />Add another plot</Button>
              </div>

              {plots.map((plot, index) => (
                <Card key={plot.id} className="border-border">
                  <CardContent className="p-5">
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <p className="font-semibold">Plot {index + 1}</p>
                        <p className="text-xs text-muted-foreground">A separate land unit under this farmer's Farm.</p>
                      </div>
                      {plots.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removePlot(plot.id)}><Trash2 className="mr-1 size-4" />Remove</Button>}
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className={fieldClass}>
                        <Label>Plot / block name</Label>
                        <Input value={plot.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePlotDraft(plot.id, { name: e.target.value })} placeholder={`Optional, e.g. Plot ${index + 1} / Home block`} />
                      </div>
                      <div className={fieldClass}>
                        <Label>Plot acreage</Label>
                        <Input type="number" min="0" step="0.1" value={plot.area} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePlotDraft(plot.id, { area: e.target.value })} placeholder="Optional" />
                      </div>
                      <div className={fieldClass}>
                        <Label>Ownership / tenure</Label>
                        <Select value={plot.ownershipType} onValueChange={(value: string) => updatePlotDraft(plot.id, { ownershipType: value })}>
                          <SelectTrigger><SelectValue placeholder="Select when known" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="OWNED">Owned</SelectItem>
                            <SelectItem value="LEASED">Leased</SelectItem>
                            <SelectItem value="HIRED">Hired / rented</SelectItem>
                            <SelectItem value="FAMILY">Family / customary land</SelectItem>
                            <SelectItem value="OTHER">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className={fieldClass}>
                        <Label>Current land use</Label>
                        <Input value={plot.currentLandUse} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePlotDraft(plot.id, { currentLandUse: e.target.value })} placeholder="Optional, e.g. Coffee, maize, fallow" />
                      </div>
                      <div className={fieldClass}>
                        <Label>Crop</Label>
                        <Input value={plot.crop} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePlotDraft(plot.id, { crop: e.target.value })} placeholder="Optional, e.g. Coffee" />
                      </div>
                      <div className={fieldClass}>
                        <Label>Variety</Label>
                        <Input value={plot.variety} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePlotDraft(plot.id, { variety: e.target.value })} placeholder="Optional, e.g. Robusta" />
                      </div>
                      <div className={fieldClass}>
                        <Label>Planting date</Label>
                        <Input type="date" value={plot.plantingDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePlotDraft(plot.id, { plantingDate: e.target.value })} />
                      </div>
                      <div className={fieldClass}>
                        <Label>Initial plants / trees</Label>
                        <Input type="number" min="0" step="1" value={plot.initialPopulation} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePlotDraft(plot.id, { initialPopulation: e.target.value })} placeholder="Optional" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <p className="rounded-lg border border-border bg-muted/45 p-3 text-xs leading-5 text-muted-foreground" data-testid="farm-persistence-note">The Farm appears in the Farms registry immediately after this farmer is saved, even while registration is still Draft. Plot acreage drives Farm acreage. Plot GPS, boundaries, mapping verification and later plant-population assessments are completed from Farms.</p>
            </div>}

            {/* Stage 7: Review */}
            {step === 6 && <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Farmer</p>
                  <p className="mt-2 font-semibold" data-testid="review-farmer-name">{name.first.trim() || "Not provided"} {name.last.trim() || ""}</p>
                  <p className="mt-1 text-sm text-muted-foreground">Gender: {sex === "NOT_STATED" ? "Not stated" : sex === "FEMALE" ? "Female" : sex === "MALE" ? "Male" : "Other"}{birthYear.trim() ? ` · Born ${birthYear.trim()}` : ""}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{selectedGroup ? `Group: ${selectedGroup.groupName} (${selectedGroup.groupCode})` : "No group specified"}</p>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Project context</p>
                  <p className="mt-2 font-semibold">ARISE</p>
                  <p className="mt-1 text-sm text-muted-foreground">ACZON Farms Ltd</p>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Location</p>
                  <p className="mt-2 font-semibold" data-testid="review-location">{districtName || "Not selected"}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{locationLabel || "Hierarchy incomplete"}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{landmark.trim() ? `Landmark: ${landmark.trim()}` : "No landmark provided"}</p>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Consent</p>
                  <p className="mt-2 font-semibold">{consentTemplate ? `${consentTemplate.consentCode} · Version ${consentTemplate.version}` : "No active template"}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{consentState === "ACCEPTED" ? "Accepted" : consentState === "DECLINED" ? "Declined" : "Not recorded"}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{consentTemplate ? `Effective from ${consentTemplate.effectiveFrom}` : "Agreement requirement will remain pending while this record stays in Draft"}</p>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contact</p>
                  <p className="mt-2 font-semibold">{normalizedPrimaryPhone || "Invalid primary phone"}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{email.trim() || "No email provided"}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{alternatePhone.trim() ? `Alternate: ${alternatePhone.trim()}` : "No alternate phone"}</p>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Household</p>
                  <p className="mt-2 font-semibold">Household size: {householdSize.trim() || "Not provided"}</p>
                  <p className="mt-1 text-sm text-muted-foreground">Total dependants: {parsedDependants ?? "Not fully recorded"} · Living here: {dependantsInHousehold.trim() || "—"} · Elsewhere: {dependantsElsewhere.trim() || "—"}</p>
                  <p className="mt-1 text-sm text-muted-foreground">Under 18: {dependantsUnder18.trim() || "—"}{householdNotes.trim() ? ` · Notes: ${householdNotes.trim()}` : ""}</p>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Responsible person</p>
                  <p className="mt-2 font-semibold">{nextOfKinFirstName.trim()} {nextOfKinLastName.trim()}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{nextOfKinRelationship || "Relationship not selected"} · {nextOfKinPhone.trim() || "No phone"}</p>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Identity & agreement evidence</p>
                  <p className="mt-2 text-sm">NIN: {ninNormalized ? `Recorded · •••• ${ninNormalized.slice(-4)}` : "Pending / not provided"}</p>
                  <p className="mt-1 text-sm text-muted-foreground">ID front: {nationalIdFront ? "Attached" : "Pending"} · ID back: {nationalIdBack ? "Attached" : "Pending"}</p>
                  <p className="mt-1 text-sm text-muted-foreground">Farmer photo: {farmerPhoto ? "Attached" : "Pending"} · Signed agreement: {signedAgreement ? "Attached" : "Pending before approval"}</p>
                </div>
                <div className="rounded-lg border border-border p-4 sm:col-span-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Plots and crop establishment summary</p>
                  <p className="mt-2 font-semibold">{farmName.trim() || `${name.first.trim()} ${name.last.trim()} Farm`}</p>
                  <p className="mt-1 text-sm text-muted-foreground">Derived Farm acreage: {derivedFarmAcreage > 0 ? `${derivedFarmAcreage.toFixed(2)} acres` : "Pending Plot acreages"} · Location: {locationLabel}</p>
                  <p className="mt-2 text-sm">{meaningfulPlots.length ? `${meaningfulPlots.length} Plot${meaningfulPlots.length === 1 ? "" : "s"} captured during registration` : "No Plot details captured yet; they can be completed from Farms."}</p>
                  {meaningfulPlots.map((plot, index) => <p key={plot.id} className="mt-1 text-sm text-muted-foreground">Plot {index + 1}: {plot.name.trim() || "Unnamed"}{plot.area.trim() ? ` · ${plot.area.trim()} acres` : ""}{plot.ownershipType ? ` · ${plot.ownershipType}` : ""}{plot.crop.trim() ? ` · ${plot.crop.trim()}` : ""}</p>)}
                </div>
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-3" data-testid="review-persistence-summary">
                <p className="text-xs leading-5 text-muted-foreground">This registration creates the Farmer Registry record and the farmer's Farm immediately. Any Plot details entered here are linked to that Farm; Farm acreage is derived from its active Plots. GPS/boundaries and later agronomic assessments are completed from Farms. If the farmer agreement is still pending, that requirement remains visible without preventing the Draft record from being saved.</p>
              </div>
              {!stageValid[6] && <p className="rounded-lg border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive" data-testid="review-incomplete-warning"><AlertCircle className="mr-2 inline size-4" />Registration is incomplete. Complete the required identity, primary phone, location, household/next-of-kin and any invalid Plot fields before saving.</p>}
            </div>}
          </section>

          <div className="flex items-center justify-between gap-3">
            <Button type="button" variant="outline" onClick={previous} disabled={step === 0} data-testid="registration-back-button"><ChevronLeft className="mr-2 size-4" />Back</Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => void saveDraft()} data-testid="registration-save-draft-button"><Save className="mr-2 size-4" />Save draft</Button>
              {step < 6 ? <Button type="button" onClick={next} disabled={!stageValid[step]} data-testid="registration-next-button">Continue<ChevronRight className="ml-2 size-4" /></Button> : <Button type="submit" disabled={!stageValid[6]} data-testid="add-farmer-submit-button"><Check className="mr-2 size-4" />Save farmer</Button>}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
